import React, { useEffect, useState } from 'react';
import { checkExpired, formatAccessToken, getCookie, setCookie } from 'stash/utils';
import {
	ACCESS_TOKEN_COOKIE_NAME,
	REFRESH_TOKEN_COOKIE_NAME,
} from 'stash/constants/session';
import { useDispatch, useSelector } from 'react-redux';
import { refreshTokenRequest } from 'stash/actions';
import { selectUUID } from 'stash/selectors';
import {
	AGENT_MESSAGE,
	ChatAnalytics,
	Message,
	TrackFunction,
	USER_MESSAGE,
	UserMessage,
} from './types';
import { ChatWindow } from './ChatWindow';
import { ChatForm } from './ChatForm';
import {
	fetchErrorHandler,
	getRandomErrorMessage,
	parseResponse,
	postChatMessage,
} from './utils';
import {
	v1_AiChat_Viewed,
	v1_AiChat_Failed,
} from '@stashinvest/product-events-javascript/lib/v1/event_static_methods';

type ChatProps = {
	initMessage?: string | null;
	focus?: string | null;
	analytics: ChatAnalytics;
	track: TrackFunction;
};

export default function Chat({ initMessage, focus, analytics, track }: ChatProps) {
	const dispatch = useDispatch();
	const uuid = useSelector(selectUUID);
	const [messages, setMessages] = useState<Message[]>([]);
	const [topics, setTopics] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		track(v1_AiChat_Viewed({ ...analytics }));
	}, [track]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setMessages((prev) => [
			...prev,
			{
				id: crypto.randomUUID(),
				text: `Hi, I'm your personal ✨Money Coach✨ here to help you out. ${
					initMessage || ''
				}`,
				type: AGENT_MESSAGE,
			},
		]);
	}, [initMessage]);

	const handleSubmit = async (text: string) => {
		const formattedText = text?.trim();
		if (!formattedText) return;

		setLoading(true);

		// ID's are for efficient rendering and message tracking
		const userMessage: UserMessage = {
			id: crypto.randomUUID(),
			text: formattedText,
			type: USER_MESSAGE,
		};

		// Add user message
		setMessages((prev) => [...prev, userMessage]);

		// Clear previous topics
		setTopics([]);

		try {
			// Refresh token logic
			const refreshToken = getCookie(REFRESH_TOKEN_COOKIE_NAME);
			const accessToken = getCookie(ACCESS_TOKEN_COOKIE_NAME);
			let newAccessToken;
			if (!!refreshToken && checkExpired(accessToken)) {
				// If this errors because refresh token is expired, will log out user.
				const res = await dispatch(refreshTokenRequest(refreshToken));
				setCookie(REFRESH_TOKEN_COOKIE_NAME, res.refresh_token);
				newAccessToken = formatAccessToken(res.access_token);
				setCookie(ACCESS_TOKEN_COOKIE_NAME, newAccessToken);
			}

			const { response, timeoutId } = await postChatMessage(
				uuid,
				formattedText,
				analytics.screen,
				focus || analytics.focus || null,
				newAccessToken || null,
				() => fetchErrorHandler(setLoading, setTopics, setMessages, track, analytics)
			);
			parseResponse(
				response,
				timeoutId,
				setLoading,
				setMessages,
				setTopics,
				track,
				analytics
			);
		} catch (error) {
			setLoading(false);
			setMessages((prev) => [...prev, getRandomErrorMessage()]);
			console.error('AI Error:', error);
			const errorMessage = (error as { message: string })?.message || '';
			track(v1_AiChat_Failed({ ...analytics, error: errorMessage }));
		}
	};
	return (
		<div
			style={{
				position: 'relative',
				height: '100%',
				width: '100%',
			}}
			className="flex flex-col"
		>
			<ChatWindow
				messages={messages}
				topics={topics}
				loading={loading}
				onSubmit={handleSubmit}
				analytics={analytics}
				track={track}
			/>
			<ChatForm
				disabled={loading}
				onSubmit={handleSubmit}
				analytics={analytics}
				track={track}
			/>
		</div>
	);
}
